import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Save, CheckCircle, AlertTriangle, Shield, Key } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { supabase } from '../../../../lib/supabase';
import { logEvent } from '../../../../services/loggingService';
import { calculatePasswordStrength  } from '../../../../utils/passwordValidation';
import type {PasswordStrength} from '../../../../utils/passwordValidation';

import { LabelValueRow } from './LabelValueRow';
import { SettingsSection } from './SettingsSection';

interface SecurityTabProps {
  grid3?: boolean;
  sectioned?: boolean;
  editing?: boolean;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ grid3, sectioned, editing = true }) => {
  const { t } = useTranslation();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    level: '',
    color: '',
    bgColor: '',
    suggestions: []
  });

  // Calculate password strength when new password changes
  React.useEffect(() => {
    if (passwordData.newPassword) {
      setPasswordStrength(calculatePasswordStrength(passwordData.newPassword));
    }
  }, [passwordData.newPassword]);

  // Handle input changes
  const handleInputChange = (field: keyof typeof passwordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Validate form
  const validateForm = () => {
    if (!passwordData.currentPassword) {
      setError(t('dashboard.userProfile.security.currentPasswordRequired'));
      return false;
    }
    
    if (!passwordData.newPassword) {
      setError(t('dashboard.userProfile.security.newPasswordRequired'));
      return false;
    }
    
    if (passwordData.newPassword.length < 6) {
      setError(t('dashboard.userProfile.security.newPasswordMinLength'));
      return false;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('dashboard.userProfile.security.passwordsDoNotMatch'));
      return false;
    }
    
    if (passwordStrength.score < 3) {
      setError(t('dashboard.userProfile.security.chooseStrongerPassword'));
      return false;
    }
    
    return true;
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Update password
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      // Clear form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccess(t('dashboard.userProfile.security.passwordChanged'));

      await logEvent({
        level: 'info',
        action: 'password_changed',
        details: {}
      });

    } catch (err) {
      console.error('Error changing password:', err);
      setError(err instanceof Error ? err.message : t('dashboard.userProfile.security.failedToChangePassword'));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full min-h-[44px] pl-10 pr-12 py-2 vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation';
  const labelClass = 'block text-sm font-medium vscode-text-secondary mb-1';

  if (sectioned) {
    return (
      <>
        {success && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] px-3 py-2 text-sm text-[var(--vscode-success)]">
            <CheckCircle className="w-4 h-4 shrink-0" /> {success}
          </div>
        )}
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2 text-sm text-amber-700/80 dark:text-amber-400/70">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500/70 dark:text-amber-400/50" /> {error}
          </div>
        )}
        <SettingsSection title={t('dashboard.userProfile.security.securityTitle')} hideRule>
          <form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }} className="space-y-2">
            <dl className="space-y-0">
              <LabelValueRow label={t('dashboard.userProfile.security.currentPassword')}>
                <div className="relative max-w-xs">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
                  <input type={showPasswords.current ? 'text' : 'password'} value={passwordData.currentPassword} onChange={(e) => handleInputChange('currentPassword', e.target.value)} className={inputClass} placeholder={t('dashboard.userProfile.security.placeholderCurrent')} autoComplete="current-password" />
                  <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-3 top-1/2 -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">{showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </LabelValueRow>
              <LabelValueRow label={t('dashboard.userProfile.security.newPassword')}>
                <div className="relative max-w-xs">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
                  <input type={showPasswords.new ? 'text' : 'password'} value={passwordData.newPassword} onChange={(e) => handleInputChange('newPassword', e.target.value)} className={inputClass} placeholder={t('dashboard.userProfile.security.placeholderNew')} autoComplete="new-password" />
                  <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-1/2 -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">{showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
                {passwordData.newPassword && passwordStrength.level && (
                  <p className="mt-1 text-xs vscode-text-tertiary">{t('dashboard.userProfile.security.strength')} <span className={passwordStrength.color}>{passwordStrength.level}</span></p>
                )}
              </LabelValueRow>
              <LabelValueRow label={t('dashboard.userProfile.security.confirmNewPassword')}>
                <div className="relative max-w-xs">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
                  <input type={showPasswords.confirm ? 'text' : 'password'} value={passwordData.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} className={inputClass} placeholder={t('dashboard.userProfile.security.placeholderConfirm')} />
                  <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-1/2 -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">{showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </LabelValueRow>
            </dl>
            <div className="pt-2 flex justify-end">
              <button type="submit" disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword} className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
              style={{ background: 'var(--brand-cta-bg)' }}>
                {loading ? <LoadingSpinner size="xs" showText={false} /> : <Save className="w-4 h-4" />}
                {loading ? t('dashboard.userProfile.security.updating') : t('dashboard.userProfile.security.updatePassword')}
              </button>
            </div>
          </form>
          <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-amber-400/70 dark:text-amber-500/50 shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-amber-700/70 dark:text-amber-400/60">{t('dashboard.userProfile.security.passwordsHashedNote')}</p>
          </div>
        </SettingsSection>
      </>
    );
  }

  const formContent = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleChangePassword();
      }}
      className={grid3 ? 'grid grid-cols-1 md:grid-cols-3 gap-4' : 'space-y-3'}
    >
      <div className={grid3 ? '' : ''}>
        <label className={labelClass}>{t('dashboard.userProfile.security.currentPassword')}</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input
            type={showPasswords.current ? 'text' : 'password'}
            autoComplete="current-password"
            value={passwordData.currentPassword}
            onChange={(e) => handleInputChange('currentPassword', e.target.value)}
            className={grid3 ? 'w-full min-h-[44px] pl-10 pr-12 py-2 vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation' : inputClass}
            placeholder={t('dashboard.userProfile.security.placeholderCurrent')}
          />
          <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-3 top-1/2 transform -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">
            {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className={grid3 ? 'md:col-span-2' : ''}>
        <label className={labelClass}>{t('dashboard.userProfile.security.newPassword')}</label>
        <div className="relative">
          <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input
            type={showPasswords.new ? 'text' : 'password'}
            value={passwordData.newPassword}
            onChange={(e) => handleInputChange('newPassword', e.target.value)}
            className={grid3 ? 'w-full min-h-[44px] pl-10 pr-12 py-2 vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation' : inputClass}
            placeholder={t('dashboard.userProfile.security.placeholderNew')}
            autoComplete="new-password"
          />
          <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-1/2 transform -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">
            {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {passwordData.newPassword && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs vscode-text-tertiary">Strength</span>
                  {passwordStrength.level && <span className={`text-xs font-medium ${passwordStrength.color}`}>{passwordStrength.level}</span>}
                </div>
                <div className="w-full h-1.5 vscode-bg-tertiary rounded-full overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${passwordStrength.bgColor}`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }} />
                </div>
              </div>
              {passwordStrength.score >= 4 && <Shield className="w-5 h-5 text-[var(--vscode-success)]" />}
            </div>
            {passwordStrength.suggestions.length > 0 && (
              <div className="vscode-bg-tertiary/50 p-2 rounded border vscode-border">
                <p className="text-xs vscode-text-accent font-medium mb-1">{t('dashboard.userProfile.security.suggestions')}</p>
                <ul className="text-xs vscode-text-secondary space-y-1">
                  {passwordStrength.suggestions.map((s, i) => (
                    <li key={i} className="flex items-center"><span className="w-1 h-1 bg-gray-400 rounded-full mr-2" />{s}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={grid3 ? 'md:col-span-3' : ''}>
        <label className={labelClass}>{t('dashboard.userProfile.security.confirmNewPassword')}</label>
        <div className="relative max-w-md">
          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input
            type={showPasswords.confirm ? 'text' : 'password'}
            value={passwordData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className={grid3 ? 'w-full min-h-[44px] pl-10 pr-12 py-2 vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation' : 'w-full pl-10 pr-12 py-2 min-h-[44px] vscode-input rounded-md vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] touch-manipulation'}
            placeholder={t('dashboard.userProfile.security.placeholderConfirm')}
          />
          <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-1/2 transform -translate-y-1/2 vscode-text-tertiary hover:vscode-text-secondary">
            {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className={grid3 ? 'md:col-span-3 flex justify-end' : ''}>
        <button
          type="submit"
          disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
          className="inline-flex items-center gap-2 min-h-[44px] px-4 py-2 text-sm font-medium rounded-md hover:opacity-90 disabled:opacity-50 transition-opacity touch-manipulation text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
          style={{ background: 'var(--brand-cta-bg)' }}
        >
          {loading ? <LoadingSpinner size="xs" showText={false} /> : <Save className="w-4 h-4" />}
          {loading ? t('dashboard.userProfile.security.updating') : t('dashboard.userProfile.security.updatePassword')}
        </button>
      </div>
    </form>
  );

  return (
    <div className="overflow-y-auto space-y-2">
      {success && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-[var(--vscode-success-bg)] border border-[var(--vscode-success-border)] rounded-lg p-2.5 text-sm">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-[var(--vscode-success)] mr-2" />
            <span className="text-[var(--vscode-success)]">{success}</span>
          </div>
        </motion.div>
      )}
      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-700/30 rounded-lg p-2.5 text-sm">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-amber-500/70 dark:text-amber-400/50 mr-2 shrink-0" />
            <span className="text-amber-700/80 dark:text-amber-400/70">{error}</span>
          </div>
        </motion.div>
      )}
      <div className={grid3 ? '' : 'max-w-md'}>{formContent}</div>
    </div>
  );
};

export default React.memo(SecurityTab);
